import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type {
  DemandTimelineResponse,
  TimelineCheckState,
  TimelineEffectiveStatus,
  TimelineGap,
  TimelineLane,
  TimelineSector,
  TimelineStation,
  WallClock
} from '@/Network/Types/Responses/DemandResponses'
import { OFF_TEAM_COLORS, OPEN_STATION_COLORS, TYPE_COLORS } from '@/utils/lensColors'
import {
  VACANCY_AWAITING_BG,
  VACANCY_AWAITING_COLOR
} from '@/modules/company/VacancyCenter/vacancyCenterColors'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import { hasCoverageContract, UNIDENTIFIED_COVERAGE_LABEL } from '@/utils/coverage'
import { UNIDENTIFIED_COVERAGE_COLORS } from '@/components/designSystem/Table/StandardRow/unidentifiedCoverageStyles'
import type { CreateStationPrefill } from '@/modules/company/Dashboard/modals/createStation/helpers'

/**
 * Modelo puro da Timeline por setor (Onda 2.3, mockup D2): posição em px, rótulos de hora,
 * empacotamento das barras, textos da raia, cartões de alerta e cores. Sem Vue, sem store.
 */

/** Pixels por hora do eixo (mockup: `x = h * 33`). */
export const HOUR_PX = 33
/** Largura da coluna fixa de rótulo no desktop (mockup: `--lab: 250px`). */
export const LABEL_PX = 250
/** Largura da coluna de rótulo no celular (< 1024px): sobra trilho para a grade. */
export const LABEL_PX_MOBILE = 150
/** Desktop da Timeline (≥ 1024px); abaixo disso a coluna de rótulo encolhe. */
export const TIMELINE_DESKTOP_QUERY = '(min-width: 1024px)'

/** Largura da coluna de rótulo conforme a tela. */
export function labelPx(isMobile: boolean): number {
  return isMobile ? LABEL_PX_MOBILE : LABEL_PX
}

// ---------------------------------------------------------------------------
// Escala do eixo (spec 2026-09-30, R4). Desktop: eixo inteiro a 33px/h (igual a hoje).
// Celular (flag): janela de 6h/12h a 48px/h ou de 24h a 20px/h, coluna de rótulo de 78px.
// ---------------------------------------------------------------------------

/** Janelas do celular (D3), na ordem do SegmentedControl. Valor em `mobilePrefs`. */
export const TIMELINE_WINDOWS = ['6', '12', '24'] as const
export type TimelineWindow = (typeof TIMELINE_WINDOWS)[number]
export const DEFAULT_TIMELINE_WINDOW: TimelineWindow = '12'
/** Celular, janelas de 6h e 12h: 12h = 576px, que rolam dentro da grade. */
export const HOUR_PX_PHONE = 48
/** Celular, janela de 24h: o dia inteiro, para leitura. */
export const HOUR_PX_PHONE_DAY = 20
/** Coluna de rótulo no layout de celular (< 768px com a flag). */
export const LABEL_PX_PHONE = 78
/** Sem "agora" (outro dia), a janela começa na troca de turno das 07h. */
export const WINDOW_DEFAULT_START_HOUR = 7

/** Trecho visível do eixo: horas a partir de `range.start` e px por hora. */
export interface TimelineScale {
  startHour: number
  endHour: number
  hourPx: number
}

export function fullScale(maxHours: number, hourPx: number = HOUR_PX): TimelineScale {
  return { startHour: 0, endHour: maxHours, hourPx }
}

/**
 * Janela do celular. 24h = eixo inteiro (com ou sem a ponte) a 20px/h; 6h/12h = recorte de
 * horas inteiras centrado em `centerHour`, sempre dentro de `[0, maxHours]`.
 */
export function windowScale(
  window: TimelineWindow,
  maxHours: number,
  centerHour: number
): TimelineScale {
  if (window === '24') return fullScale(maxHours, HOUR_PX_PHONE_DAY)
  const span = Math.min(Number(window), maxHours)
  const start = Math.min(Math.max(Math.round(centerHour - span / 2), 0), maxHours - span)
  return { startHour: start, endHour: start + span, hourPx: HOUR_PX_PHONE }
}

/** Centro padrão da janela: o "agora" (hoje) ou 07h + meia janela (outro dia). */
export function defaultWindowCenter(
  now: WallClock | null,
  rangeStart: WallClock,
  window: TimelineWindow
): number {
  if (now) return hoursFromRangeStart(now, rangeStart)
  return WINDOW_DEFAULT_START_HOUR + Number(window) / 2
}

/** ‹ ›: move meia janela, com o centro preso para a janela não sair do eixo. */
export function shiftWindowCenter(
  center: number,
  window: TimelineWindow,
  maxHours: number,
  direction: 1 | -1
): number {
  const span = Math.min(Number(window), maxHours)
  const next = center + (direction * span) / 2
  return Math.min(Math.max(next, span / 2), maxHours - span / 2)
}

export function scaleX(hours: number, scale: TimelineScale): number {
  const clamped = Math.min(Math.max(hours, scale.startHour), scale.endHour)
  return (clamped - scale.startHour) * scale.hourPx
}

export function scaleWidth(scale: TimelineScale): number {
  return (scale.endHour - scale.startHour) * scale.hourPx
}

export function scaleContains(hours: number, scale: TimelineScale): boolean {
  return hours >= scale.startHour && hours <= scale.endHour
}

/** Horas inteiras dentro da escala (marcas do eixo e linhas da grade). */
export function scaleHours(scale: TimelineScale): number[] {
  const first = Math.ceil(scale.startHour)
  const last = Math.floor(scale.endHour)
  return Array.from({ length: Math.max(last - first + 1, 0) }, (_, i) => first + i)
}

/**
 * `scrollLeft` que põe `hours` no meio da área do trilho (a coluna de rótulo é sticky e
 * cobre `labelPx` à esquerda). Nunca negativo.
 */
export function centerScrollLeft(
  hours: number,
  scale: TimelineScale,
  clientWidth: number,
  labelPx: number
): number {
  return Math.max(0, scaleX(hours, scale) - (clientWidth - labelPx) / 2)
}

/** Arredonda px em 1 casa (evita `318.99999…` de dízimas como 9h40 × 33). */
const roundPx = (px: number): number => Math.round(px * 10) / 10

const MINUTE_MS = 60_000

/** `'yyyy-MM-ddTHH:mm'` → minutos desde a época, lido como UTC (relógio de parede, sem DST). */
function wallMinutes(wall: WallClock): number {
  const [datePart, timePart = '00:00'] = wall.split('T')
  const [y, mo, d] = datePart.split('-').map(Number)
  const [h, mi] = timePart.split(':').map(Number)
  return Date.UTC(y, mo - 1, d, h, mi) / MINUTE_MS
}

const datePartOf = (wall: WallClock): string => wall.slice(0, 10)

/** Horas (fracionárias) entre o início do intervalo e `wall`; pode ser negativo ou > 31. */
export function hoursFromRangeStart(wall: WallClock, rangeStart: WallClock): number {
  return (wallMinutes(wall) - wallMinutes(rangeStart)) / 60
}

/** Posição em px no eixo inteiro, cortada em `[0, maxHours]` (31 com a ponte, 24 sem). */
export function xOf(hours: number, maxHours: number): number {
  return scaleX(hours, fullScale(maxHours))
}

/**
 * Trecho `[start, end)` numa escala, cortado nas bordas: `{ left, width }` em px, ou `null`
 * quando cai inteiro fora (ex.: plantão só na ponte com o eixo em 24h, ou fora da janela).
 */
export function scaleSpanPx(
  start: WallClock,
  end: WallClock,
  rangeStart: WallClock,
  scale: TimelineScale
): { left: number; width: number } | null {
  const startHours = hoursFromRangeStart(start, rangeStart)
  const endHours = hoursFromRangeStart(end, rangeStart)
  if (endHours <= scale.startHour || startHours >= scale.endHour || endHours <= startHours) {
    return null
  }
  const left = roundPx(scaleX(startHours, scale))
  return { left, width: roundPx(scaleX(endHours, scale)) - left }
}

/** Mesmo que `scaleSpanPx` no eixo inteiro do desktop (`fullScale(maxHours)`). */
export function spanPx(
  start: WallClock,
  end: WallClock,
  rangeStart: WallClock,
  maxHours: number
): { left: number; width: number } | null {
  return scaleSpanPx(start, end, rangeStart, fullScale(maxHours))
}

function hourLabel(hours: number, minutes: number): string {
  const hh = String(hours).padStart(2, '0')
  return minutes ? `${hh}h${String(minutes).padStart(2, '0')}` : `${hh}h`
}

/** `'07h'` / `'07h30'`. */
export function formatHour(wall: WallClock): string {
  const [h, m] = wall.slice(11, 16).split(':').map(Number)
  return hourLabel(h, m)
}

/** Diferença em dias de calendário entre a data de `wall` e `date`. */
function dayOffset(wall: WallClock, date: string): number {
  return Math.round(
    (wallMinutes(`${datePartOf(wall)}T00:00`) - wallMinutes(`${date}T00:00`)) / 1440
  )
}

function dayPrefix(offset: number): string {
  if (offset < 0) return 'ontem '
  if (offset > 0) return 'amanhã '
  return ''
}

/** Trecho curto (cartões, etiquetas; `hh` do mockup): `'07h–09h'`, `'19h–07h'`, `'13h–00h'`. */
export function formatSpan(start: WallClock, end: WallClock): string {
  return `${formatHour(start)}–${formatHour(end)}`
}

/** Trecho do tooltip, com o dia quando difere de `date`: `'07h → 19h'`, `'ontem 19h → 07h'`. */
export function formatSpanVerbose(start: WallClock, end: WallClock, date: string): string {
  const startText = dayPrefix(dayOffset(start, date)) + formatHour(start)
  const endText = dayPrefix(dayOffset(end, date)) + formatHour(end)
  return `${startText} → ${endText}`
}

/**
 * Empacotamento *first-fit* por ordem de início: cada item vai para a primeira sub-linha cujo
 * último fim é ≤ ao seu início; senão abre uma sub-linha nova.
 */
export function packRows<T extends { start: string; end: string }>(items: T[]): T[][] {
  const sorted = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.start.localeCompare(b.item.start) || a.index - b.index)
  const rows: T[][] = []
  const lastEnd: string[] = []
  sorted.forEach(({ item }) => {
    const row = lastEnd.findIndex((end) => end <= item.start)
    if (row === -1) {
      rows.push([item])
      lastEnd.push(item.end)
    } else {
      rows[row].push(item)
      lastEnd[row] = item.end
    }
  })
  return rows
}

type TargetRule = NonNullable<TimelineLane['target']>['rules'][number]

function ruleWindowText(rule: TargetRule): string {
  const length = (((rule.windowEndHour - rule.windowStartHour) % 24) + 24) % 24
  const window =
    length === 0
      ? '24h'
      : `${hourLabel(rule.windowStartHour % 24, 0)}–${hourLabel(rule.windowEndHour % 24, 0)}`
  return `${window} × ${rule.simultaneous}`
}

/** Exigência da raia no sub-rótulo. */
export function laneRequirementText(lane: TimelineLane): string {
  const rules = lane.target?.rules ?? []
  if (rules.length === 1) return `exige ${ruleWindowText(rules[0])}`
  if (rules.length > 1) return `meta: ${rules.map(ruleWindowText).join('; ')}`
  if (lane.agenda === 'CONTINUOUS') return 'exige 24h'
  return 'agenda variável · sem buraco'
}

/**
 * `'2 posições · Intensivistas BNG'` (escalas separadas por vírgula). Raia sem plantões
 * (`positions = 0`, só quadro-meta) omite as posições: só as escalas, ou `''` sem escalas.
 */
export function laneSubtitle(lane: TimelineLane): string {
  const positions =
    lane.positions > 0 ? `${lane.positions} ${lane.positions === 1 ? 'posição' : 'posições'}` : ''
  const schedules = lane.schedules.map((s) => s.name).join(', ')
  return [positions, schedules].filter(Boolean).join(' · ')
}

export function missingText(n: number): string {
  return n === 1 ? 'falta 1 médico' : `faltam ${n} médicos`
}

export interface TimelineAlert {
  kind: 'absolute' | 'relative' | 'open'
  id: string
  sectorName: string
  specialtyName: string
  span: string
  text: string
}

/** Buraco tocado no celular: o sheet precisa do setor e da raia para montar o texto. */
export interface TimelineGapSelection {
  sector: TimelineSector
  lane: TimelineLane
  gap: TimelineGap
}

/** Alvo de toque mínimo da spec (critério 3). */
export const TAP_TARGET_PX = 40
/** Altura visual da barra (`.tl-bar` em TimelineBar.vue). */
export const BAR_HEIGHT_PX = 17

/** Caixa de toque invisível da barra: 40px de altura centrada nela e largura ≥ 40px. */
export function barHitBox(
  bar: { left: number; width: number },
  top: number
): { left: number; top: number; width: number; height: number } {
  const width = Math.max(bar.width, TAP_TARGET_PX)
  return {
    left: bar.left - (width - bar.width) / 2,
    top: top + BAR_HEIGHT_PX / 2 - TAP_TARGET_PX / 2,
    width,
    height: TAP_TARGET_PX
  }
}

/** Troca tudo fora de `[A-Za-z0-9-]` por `_<hex>`: seguro para `id` e sem colisão. */
function safeIdPart(value: string): string {
  return value.replace(/[^A-Za-z0-9-]/g, (c) => `_${c.charCodeAt(0).toString(16)}`)
}

/** `id` HTML estável do buraco (destaque a partir do cartão). */
export function gapElementId(laneKey: string, gap: TimelineGap): string {
  return `tl-gap-${safeIdPart(laneKey)}-${gap.kind}-${safeIdPart(gap.start)}`
}

/** `id` HTML estável da barra do plantão. */
export function stationElementId(stationId: number): string {
  return `tl-station-${stationId}`
}

/**
 * Ocupado sem identificação: o `state` do BE nasce de `doctor_id IS NULL` e marcaria
 * `open`. Com `coverageStatus` (ou, sem ele, `unregisteredDoctor`) o front o trata como
 * coberto — nunca vaga, nunca alerta de "sem médico".
 */
export function isTimelineUnidentified(station: TimelineStation): boolean {
  if (hasCoverageContract(station)) return station.coverageStatus === CoverageStatus.UNIDENTIFIED
  return station.unregisteredDoctor === true && !station.doctorName
}

/** `state` efetivo da barra: UNIDENTIFIED conta como coberto (`filled`). */
export function timelineStationState(station: TimelineStation): TimelineStation['state'] {
  return isTimelineUnidentified(station) ? 'filled' : station.state
}

/** Estado da barra no tooltip (linha abaixo do tipo). */
export const TIMELINE_STATE_LABELS: Record<TimelineStation['state'], string> = {
  open: 'Plantão criado, sem médico',
  awaiting: 'Aguardando confirmação',
  filled: 'Médico confirmado'
}

/**
 * Linha de estado do tooltip. No ocupado o título já é "Ocupado sem identificação": a
 * linha mostra o responsável pela cobertura quando o dado trouxer, senão some (Minor 8).
 */
export function timelineTipStatusLine(
  station: TimelineStation,
  state: TimelineStation['state']
): string | null {
  if (isTimelineUnidentified(station)) {
    const responsible = station.coverageResponsibleName?.trim()
    return responsible ? `Responsável pela cobertura: ${responsible}` : null
  }
  return TIMELINE_STATE_LABELS[state]
}

/** A resposta da timeline traz algum ocupado sem identificação (legenda do topo). */
export function timelineHasUnidentified(res: DemandTimelineResponse | null | undefined): boolean {
  return (res?.sectors ?? []).some((sector) =>
    sector.lanes.some((lane) => lane.stations.some(isTimelineUnidentified))
  )
}

/** Quem cobre o plantão: nunca "Dr(a)." sozinho; UNIDENTIFIED tem rótulo próprio. */
export function timelineDoctorLabel(station: TimelineStation): string {
  if (isTimelineUnidentified(station)) return UNIDENTIFIED_COVERAGE_LABEL
  return station.doctorName ? `Dr(a). ${station.doctorName}` : 'Médico sem nome'
}

const ALERT_ORDER: Record<TimelineAlert['kind'], number> = { absolute: 0, relative: 1, open: 2 }

/**
 * Cartões de alerta: buracos absolutos → relativos → vagas abertas, cada grupo por início.
 * Hoje (`now` não nulo) as vagas já terminadas saem. `showGaps = false` → só vagas.
 * `showBridge = false` (eixo cortado em 24h): sai o que começa na ponte (D+1 00h–07h), que
 * não aparece na grade e não teria o que destacar.
 */
export function buildAlerts(
  res: DemandTimelineResponse,
  opts: { showGaps: boolean; showBridge?: boolean }
): TimelineAlert[] {
  const entries: { alert: TimelineAlert; start: string }[] = []
  res.sectors.forEach((sector) => {
    const sectorName = sector.sectorName ?? 'Sem setor'
    sector.lanes.forEach((lane) => {
      const base = { sectorName, specialtyName: lane.specialtyName }
      if (opts.showGaps) {
        lane.gaps.forEach((gap) => {
          entries.push({
            start: gap.start,
            alert: {
              ...base,
              kind: gap.kind,
              id: gapElementId(lane.key, gap),
              span: formatSpan(gap.start, gap.end),
              text: gap.kind === 'absolute' ? 'nenhum plantão criado' : missingText(gap.missing)
            }
          })
        })
      }
      lane.stations
        .filter((s) => timelineStationState(s) === 'open' && (res.now === null || s.end > res.now))
        .forEach((s) => {
          entries.push({
            start: s.start,
            alert: {
              ...base,
              kind: 'open',
              id: stationElementId(s.stationId),
              span: formatSpan(s.start, s.end),
              text: 'plantão criado, sem médico'
            }
          })
        })
    })
  })
  const visible =
    opts.showBridge === false
      ? entries.filter((entry) => hoursFromRangeStart(entry.start, res.range.start) < 24)
      : entries
  return visible
    .map((entry, index) => ({ ...entry, index }))
    .sort(
      (a, b) =>
        ALERT_ORDER[a.alert.kind] - ALERT_ORDER[b.alert.kind] ||
        a.start.localeCompare(b.start) ||
        a.index - b.index
    )
    .map((entry) => entry.alert)
}

const CHECK_LABELS: Record<TimelineCheckState, string> = {
  checked_in: '✓ check-in',
  awaiting_checkin: 'aguardando check-in',
  late: 'atrasado',
  not_required: 'sem check exigido'
}

/** Estado curto do check na barra (Q29). */
export function checkLabel(check: TimelineCheckState | null): string | null {
  return check ? CHECK_LABELS[check] : null
}

const EFFECTIVE_LABELS: Record<TimelineEffectiveStatus, string> = {
  confirmed: 'Confirmado',
  no_record: 'Sem registro',
  divergent: 'Divergente'
}

export function effectiveLabel(status: TimelineEffectiveStatus): string {
  return EFFECTIVE_LABELS[status]
}

/** Faixa do efetivo (Q28). */
export const EFFECTIVE_COLORS: Record<TimelineEffectiveStatus, string> = {
  confirmed: '#198038',
  no_record: '#BCBCCD',
  divergent: '#DA1E28'
}

export interface BarColors {
  bg: string
  border: string
  text: string
  variant: 'type' | 'open' | 'awaiting' | 'offTeam' | 'unidentified'
}

/** Cor do texto das barras de tipo (mockup D2). */
const TYPE_TEXT_COLORS: Record<keyof typeof TYPE_COLORS, string> = {
  FIXO: '#163D45',
  COBERTURA: '#7D2A44',
  EXTRA: '#0F5025'
}

/** Vaga aberta → laranja; aguardando → violeta; fora da equipe → cinza; senão cor do tipo. */
/** Hachura cinza do card da grade (mockup M1-C), em faixas finas para a barra de 17px. */
export const TIMELINE_UNIDENTIFIED_BG = `repeating-linear-gradient(135deg, ${UNIDENTIFIED_COVERAGE_COLORS.stripeLight} 0 4px, ${UNIDENTIFIED_COVERAGE_COLORS.stripeDark} 4px 8px)`

export function barColors(station: TimelineStation): BarColors {
  if (isTimelineUnidentified(station)) {
    return {
      bg: TIMELINE_UNIDENTIFIED_BG,
      border: UNIDENTIFIED_COVERAGE_COLORS.accent,
      text: UNIDENTIFIED_COVERAGE_COLORS.text,
      variant: 'unidentified'
    }
  }
  const state = timelineStationState(station)
  if (state === 'open') {
    return {
      bg: OPEN_STATION_COLORS.bg,
      border: OPEN_STATION_COLORS.stripe,
      text: '#B8650B',
      variant: 'open'
    }
  }
  if (state === 'awaiting') {
    return {
      bg: VACANCY_AWAITING_BG,
      border: VACANCY_AWAITING_COLOR,
      text: VACANCY_AWAITING_COLOR,
      variant: 'awaiting'
    }
  }
  if (station.ourTeam === false) {
    return {
      bg: OFF_TEAM_COLORS.bg,
      border: OFF_TEAM_COLORS.stripe,
      text: '#6B6B80',
      variant: 'offTeam'
    }
  }
  const type = station.type === 'COBERTURA' || station.type === 'EXTRA' ? station.type : 'FIXO'
  return {
    bg: TYPE_COLORS[type].bg,
    border: TYPE_COLORS[type].stripe,
    text: TYPE_TEXT_COLORS[type],
    variant: 'type'
  }
}

/** Conectivos ignorados nas iniciais ("Unidade de Terapia" → "UT"). */
const STOP_WORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'e'])

/** Ícone do setor: 2 letras. Várias palavras → iniciais das 2 primeiras; uma → 2 primeiras letras. */
export function sectorInitials(name: string | null): string {
  const words = (name ?? '').split(/[\s-]+/).filter((w) => w && !STOP_WORDS.has(w.toLowerCase()))
  if (words.length === 0) return '—'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

/** O dia pedido é hoje na unidade (o BE só manda `now` nesse caso). */
export function isToday(res: DemandTimelineResponse): boolean {
  return res.now !== null
}

/** Conteúdo do tooltip (barra ou buraco): título em negrito + linhas. */
export interface TimelineTipContent {
  title: string
  lines: string[]
}

/** Horas em pt-BR, sem zeros inúteis: `2` → `'2'`, `1.5` → `'1,5'`. */
export function formatHoursValue(hours: number): string {
  return String(Math.round(hours * 10) / 10).replace('.', ',')
}

// ---------------------------------------------------------------------------
// Celular (R4): sheet do buraco, resumo dos alertas e localização de itens.
// ---------------------------------------------------------------------------

/** `'2026-09-17T13:00'` → `'17/09/2026'` (relógio de parede, sem fuso). */
export function wallToFormDate(wall: WallClock): string {
  const [y, m, d] = wall.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

/** `'2026-09-17T13:00'` → `'13:00'`. */
export function wallToFormHour(wall: WallClock): string {
  return wall.slice(11, 16)
}

/** `'Quinta-feira, 17 set'` para a data de `wall`. */
function sheetDayLabel(wall: WallClock): string {
  const [y, m, d] = wall.slice(0, 10).split('-').map(Number)
  const text = format(new Date(y, m - 1, d), 'EEEE, d MMM', { locale: ptBR })
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export interface TimelineGapSheet {
  title: string
  subtitle: string
  unitName: string
  laneText: string
  /** `'3 de 4 no quadro-meta'`; `null` sem quadro-meta. */
  coverage: string | null
  cause: string
  causeStation: { stationId: number; label: string } | null
  createLabel: string
  prefill: CreateStationPrefill
}

/** Médicos exigidos no trecho do buraco (maior `need` de `required` que o cruza). */
function neededIn(lane: TimelineLane, gap: TimelineGap): number {
  const overlapping = lane.required.filter((r) => r.start < gap.end && r.end > gap.start)
  return overlapping.reduce((max, r) => Math.max(max, r.need), 0)
}

/**
 * Plantão que explica o buraco: o que termina quando ele começa ("sai às") ou, sem esse, o
 * que começa quando ele termina ("entra às"). Vaga aberta não conta (não cobre).
 */
function gapCause(
  lane: TimelineLane,
  gap: TimelineGap
): { station: TimelineStation; text: string } | null {
  const covering = lane.stations.filter((s) => timelineStationState(s) !== 'open')
  const leaving = covering.find((s) => s.end === gap.start)
  if (leaving)
    return {
      station: leaving,
      text: `${timelineDoctorLabel(leaving)} sai às ${formatHour(gap.start)}`
    }
  const arriving = covering.find((s) => s.start === gap.end)
  if (arriving)
    return {
      station: arriving,
      text: `${timelineDoctorLabel(arriving)} entra às ${formatHour(gap.end)}`
    }
  return null
}

function viewStationLabel(station: TimelineStation): string {
  return station.doctorName && !isTimelineUnidentified(station)
    ? `Ver plantão de ${timelineDoctorLabel(station)}`
    : `Ver plantão ${formatSpan(station.start, station.end)}`
}

/**
 * Prefill do "Criar plantão": escala só quando a raia tem uma; setor só quando existe e não
 * está arquivado. O fim vem da data do fim do buraco (ponte → D+1).
 */
function gapPrefill(
  res: DemandTimelineResponse,
  { sector, lane, gap }: TimelineGapSelection,
  companyId: number | null
): CreateStationPrefill {
  return {
    companyId,
    healthUnitId: res.healthUnit.id,
    scheduleId: lane.schedules.length === 1 ? lane.schedules[0].id : null,
    date: wallToFormDate(gap.start),
    endDate: wallToFormDate(gap.end),
    startHour: wallToFormHour(gap.start),
    endHour: wallToFormHour(gap.end),
    sector:
      sector.sectorId !== null && !sector.sectorArchived
        ? { id: sector.sectorId, name: sector.sectorName ?? 'Sem setor' }
        : null
  }
}

export function buildGapSheet(
  res: DemandTimelineResponse,
  selection: TimelineGapSelection,
  companyId: number | null
): TimelineGapSheet {
  const { sector, lane, gap } = selection
  const sectorName = sector.sectorName ?? 'Sem setor'
  const span = formatSpan(gap.start, gap.end)
  const duration = formatHoursValue(hoursFromRangeStart(gap.end, gap.start))
  const need = lane.target ? neededIn(lane, gap) : 0
  const covered = gap.kind === 'absolute' ? 0 : Math.max(need - gap.missing, 0)
  const cause = gapCause(lane, gap)
  return {
    title: `${gap.kind === 'absolute' ? 'Buraco' : 'Abaixo da meta'} · ${sectorName}`,
    subtitle: `${sheetDayLabel(gap.start)} · ${span} · ${duration}h`,
    unitName: res.healthUnit.name,
    laneText: `${sectorName} › ${lane.specialtyName}`,
    coverage: lane.target && need > 0 ? `${covered} de ${need} no quadro-meta` : null,
    cause:
      cause?.text ??
      (gap.kind === 'absolute' ? 'Nenhum plantão criado nesse horário' : missingText(gap.missing)),
    causeStation: cause
      ? { stationId: cause.station.stationId, label: viewStationLabel(cause.station) }
      : null,
    createLabel: `Criar vaga ${span}`,
    prefill: gapPrefill(res, selection, companyId)
  }
}

/**
 * Buracos (todas as raias) que cruzam `[fromHour, fromHour + hours)`. `showBridge = false`:
 * sai o que começa na ponte (início ≥ 24h), igual ao filtro do `buildAlerts`.
 */
export function countGapsAhead(
  res: DemandTimelineResponse,
  fromHour: number,
  hours: number,
  opts: { showBridge?: boolean } = {}
): number {
  const until = fromHour + hours
  return res.sectors
    .flatMap((sector) => sector.lanes.flatMap((lane) => lane.gaps))
    .filter((gap) => {
      const start = hoursFromRangeStart(gap.start, res.range.start)
      const end = hoursFromRangeStart(gap.end, res.range.start)
      if (opts.showBridge === false && start >= 24) return false
      return end > fromHour && start < until
    }).length
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** Cabeçalho do cartão recolhível dos alertas no celular. */
export function alertsSummaryText(
  res: DemandTimelineResponse,
  alerts: TimelineAlert[],
  window: TimelineWindow,
  opts: { showGaps: boolean; showBridge?: boolean }
): string {
  if (opts.showGaps && res.now) {
    const n = countGapsAhead(res, hoursFromRangeStart(res.now, res.range.start), Number(window), {
      showBridge: opts.showBridge
    })
    if (n > 0) return `${plural(n, 'buraco', 'buracos')} nas próximas ${window}h`
  }
  if (opts.showGaps && !res.now) {
    const n = alerts.filter((alert) => alert.kind !== 'open').length
    if (n > 0) return `${plural(n, 'buraco', 'buracos')} no dia`
  }
  return plural(alerts.length, 'alerta', 'alertas')
}

/** Início e fim do buraco ou da barra com esse `id` HTML (move a janela até ele). */
export function timelineItemSpan(
  res: DemandTimelineResponse,
  elementId: string
): { start: WallClock; end: WallClock } | null {
  for (const sector of res.sectors) {
    for (const lane of sector.lanes) {
      const gap = lane.gaps.find((g) => gapElementId(lane.key, g) === elementId)
      if (gap) return { start: gap.start, end: gap.end }
      const station = lane.stations.find((s) => stationElementId(s.stationId) === elementId)
      if (station) return { start: station.start, end: station.end }
    }
  }
  return null
}
